import React, { useEffect, useRef, useState } from 'react';
import { ZdsAlert, ZdsButton, ZdsIcon, ZdsInputText, ZdsSelect } from '@zig-design-system/ui-components-react';
import { validate, validatePostalCode, ValidationType } from '../../utilitity/validation';
import { countries } from '../../utilitity/countries';
import Translation from '../../../Shared/components/Translation';
var ErrorState;
(function (ErrorState) {
    ErrorState[ErrorState["NONE"] = 0] = "NONE";
    ErrorState[ErrorState["NOT_FOUND"] = 1] = "NOT_FOUND";
    ErrorState[ErrorState["ERROR"] = 2] = "ERROR";
})(ErrorState || (ErrorState = {}));
export const Address = ({ field, onChange, value }) => {
    const [postalCode, setPostalCode] = useState('');
    const postalCodeRef = useRef(null);
    const [houseNumber, setHouseNumber] = useState('');
    const houseNumberRef = useRef(null);
    const [houseNumberAddition, setHouseNumberAddition] = useState('');
    const [street, setStreet] = useState('');
    const [city, setCity] = useState('');
    const [country, setCountry] = useState('Netherlands');
    const [error, setError] = useState(ErrorState.NONE);
    const [addressHasValue, setAddressHasValue] = useState(!!value);
    const changeAddress = ({ newPostalCode = postalCode, newHouseNumber = houseNumber, newHouseNumberAddition = houseNumberAddition, newStreet = street, newCity = city, newCountry = country, }) => {
        const fullStreet = [newStreet, newHouseNumber, newHouseNumberAddition].join(' ').trim();
        const fullPostalCode = [newPostalCode, newCity].join(' ');
        const address = [fullStreet, fullPostalCode];
        if (field.showCountry) {
            address.push(newCountry);
        }
        onChange(field, address.join('\n'));
    };
    const lookupAddress = (postalCode, houseNumber) => {
        if (!houseNumber || !validatePostalCode(postalCode) || country !== 'Netherlands') {
            changeAddress({
                newPostalCode: postalCode,
                newHouseNumber: houseNumber,
            });
            return;
        }
        window.hexia.address
            .find(postalCode, houseNumber)
            .then((response) => {
            setStreet(response.data.street);
            setCity(response.data.city);
            setError(ErrorState.NONE);
            changeAddress({
                newPostalCode: postalCode,
                newHouseNumber: houseNumber,
                newStreet: response.data.street,
                newCity: response.data.city,
            });
        })
            .catch((error) => {
            var _a;
            setStreet('');
            setCity('');
            if (((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) === 404) {
                setError(ErrorState.NOT_FOUND);
            }
            else {
                setError(ErrorState.ERROR);
            }
        });
    };
    const handlePostalCodeBlur = (e) => {
        if (validatePostalCode(e.target.value) || country !== 'Netherlands') {
            const newPostalCode = e.target.value
                .replace(/\s/g, '')
                .replace(/(\d{4})([a-z]{2})/i, '$1 $2')
                .toUpperCase()
                .trim();
            setPostalCode(newPostalCode);
            lookupAddress(newPostalCode, houseNumber);
        }
    };
    const handleHouseNumberBlur = (e) => {
        var _a;
        // Remove every non-digit character (from prefill) and validate again
        e.target.value = e.target.value.replace(/\D/g, '');
        (_a = houseNumberRef.current) === null || _a === void 0 ? void 0 : _a.validate();
        setHouseNumber(e.target.value);
        lookupAddress(postalCode, e.target.value);
    };
    const handleStreetChange = (e) => {
        setStreet(e.target.value);
        changeAddress({ newStreet: e.target.value });
    };
    const handleCityChange = (e) => {
        setCity(e.target.value);
        changeAddress({ newCity: e.target.value });
    };
    const handleHouseNumberAdditionChange = (e) => {
        setHouseNumberAddition(e.target.value);
        changeAddress({ newHouseNumberAddition: e.target.value });
    };
    const handleCountryChange = (e) => {
        setCountry(e.target.value);
        changeAddress({ newCountry: e.target.value });
    };
    const handleChangeAddress = () => {
        setAddressHasValue(false);
        changeAddress({});
    };
    useEffect(() => {
        var _a;
        const houseNumberValidation = (event) => {
            event.detail.validationState = validate(ValidationType.DIGITS)(event.detail.value);
        };
        (_a = houseNumberRef.current) === null || _a === void 0 ? void 0 : _a.addEventListener('zdsValidate', houseNumberValidation);
        return () => {
            var _a;
            (_a = houseNumberRef.current) === null || _a === void 0 ? void 0 : _a.removeEventListener('zdsValidate', houseNumberValidation);
        };
    }, []);
    useEffect(() => {
        var _a, _b;
        const postalCodeValidation = (event) => {
            event.detail.validationState = validate(ValidationType.POSTAL_CODE)(event.detail.value);
        };
        if (country === 'Netherlands') {
            (_a = postalCodeRef.current) === null || _a === void 0 ? void 0 : _a.addEventListener('zdsValidate', postalCodeValidation);
        }
        else {
            (_b = postalCodeRef.current) === null || _b === void 0 ? void 0 : _b.removeEventListener('zdsValidate', postalCodeValidation);
        }
        return () => {
            var _a;
            (_a = postalCodeRef.current) === null || _a === void 0 ? void 0 : _a.removeEventListener('zdsValidate', postalCodeValidation);
        };
    }, [country]);
    const required = (field.mandatory && !addressHasValue) || undefined;
    return (React.createElement("div", { className: `powermail_fieldwrap_${field.marker} powermail-address` },
        React.createElement("div", { className: "zds-flex-col zds-gap-y-4" },
            !field.hideTitle && (React.createElement("div", { className: "zds-margin-top-6 zds-padding-bottom-2 powermail-subheading zds-flex zds-items-center zds-gap-2" },
                React.createElement("h5", { className: "zds-headline-sm" }, field.title),
                field.description && (React.createElement(ZdsIcon, { id: `powermail_field_${field.marker}_info`, library: "fa-solid", name: "circle-info", size: "sm" })))),
            addressHasValue && (React.createElement("div", { className: "powermail-address-summary" },
                React.createElement("span", { style: { whiteSpace: 'pre-line' } }, value),
                React.createElement("div", { className: "zds-margin-top-xs" },
                    React.createElement(ZdsButton, { variant: "outlined", className: "powermail-address__change-address-button", onZdsClick: handleChangeAddress },
                        React.createElement(Translation, { item: "powermail.address.change-address-button" }))))),
            React.createElement("div", { className: `powermail-address-input zds-flex-col zds-gap-y-4 ${addressHasValue ? 'zds-hidden' : ''}` },
                field.showCountry && (React.createElement(ZdsSelect, { label: Translation({ item: 'powermail.address.country' }), className: "powermail-address__country", id: `powermail_field_${field.marker}_country`, onZdsChange: handleCountryChange, value: country, required: required }, Object.entries(countries).map(([label, value]) => (React.createElement("option", { key: `${field.name}_${value}`, value: value }, label))))),
                React.createElement("div", { className: "zds-flex zds-gap-y-4 zds-gap-x-6 zds-flex-col sm:zds-flex-row" },
                    React.createElement(ZdsInputText, { className: "powermail-address__postal-code", label: Translation({ item: 'powermail.address.postalCode' }), placeholder: Translation({ item: 'powermail.address.postalCodePlaceholder' }), required: country === 'Netherlands' && required, value: postalCode, id: `powermail_field_${field.marker}_postal_code`, onZdsChange: (e) => setPostalCode(e.target.value), onZdsBlur: handlePostalCodeBlur, ref: postalCodeRef, autocomplete: "postal-code" },
                        React.createElement("span", { slot: "validation-message", "data-reason": "invalidPostalCode" },
                            React.createElement(Translation, { item: 'powermail.error.invalidPostalCode' }))),
                    React.createElement(ZdsInputText, { className: "powermail-address__house-number", label: Translation({ item: 'powermail.address.houseNumber' }), placeholder: Translation({ item: 'powermail.address.houseNumberPlaceholder' }), required: country === 'Netherlands' && required, value: houseNumber, id: `powermail_field_${field.marker}_house_number`, onZdsChange: (e) => setHouseNumber(e.target.value), onZdsBlur: handleHouseNumberBlur, ref: houseNumberRef, autocomplete: "address-line1" },
                        React.createElement("span", { slot: "validation-message", "data-reason": "invalidDigits" },
                            React.createElement(Translation, { item: 'powermail.error.invalidDigits' }))),
                    React.createElement(ZdsInputText, { className: "powermail-address__house-number-addition", label: Translation({ item: 'powermail.address.houseNumberAddition' }), placeholder: Translation({ item: 'powermail.address.houseNumberAdditionPlaceholder' }), value: houseNumberAddition, onZdsChange: handleHouseNumberAdditionChange, id: `powermail_field_${field.marker}_house_number_addition` })),
                error === ErrorState.NOT_FOUND && (React.createElement(ZdsAlert, { className: "powermail-address__not-found-message", type: "warning" },
                    React.createElement(Translation, { item: 'powermail.address.notFoundMessage' }))),
                React.createElement(ZdsInputText, { className: "powermail-address__street", name: "powermail-address__street", label: Translation({ item: 'powermail.address.street' }), value: street, onZdsChange: handleStreetChange, required: required, id: `powermail_field_${field.marker}_street`, readonly: error === ErrorState.NONE && country === 'Netherlands' ? true : undefined, autocomplete: "address-line1" }),
                React.createElement(ZdsInputText, { className: "powermail-address__city", name: "powermail-address__city", id: `powermail_field_${field.marker}_city`, label: Translation({ item: 'powermail.address.city' }), value: city, onZdsChange: handleCityChange, required: required, readonly: error === ErrorState.NONE && country === 'Netherlands' ? true : undefined, autocomplete: "address-level2" })))));
};
